/**
 * Discount caps per role — defaults + a settings-aware overlay.
 *
 * Phase 4.4 made the cashier + supervisor caps configurable per
 * branch in BranchSettings. UI code that needs the live cap should
 * read it via `useSettings()` and pass an overrides object to
 * `maxDiscountForRole` / `capLabelForRole`.
 *
 * MANAGER+ remains unlimited by design.
 */

import type { UserRole } from '../hooks/useAuth';

export const DEFAULT_DISCOUNT_CAP_BPS: Record<UserRole, number> = {
  CASHIER:    1000,   // 10%
  SUPERVISOR: 2000,   // 20%
  MANAGER:   10000,
  ADMIN:     10000,
  OWNER:     10000,
};

/** Back-compat alias — some callers still reference this name. */
export const DISCOUNT_CAP_BPS = DEFAULT_DISCOUNT_CAP_BPS;

export interface DiscountCapOverrides {
  cashierBps?: number;
  supervisorBps?: number;
}

function capForRole(role: UserRole, overrides?: DiscountCapOverrides): number {
  if (role === 'CASHIER')    return overrides?.cashierBps    ?? DEFAULT_DISCOUNT_CAP_BPS.CASHIER;
  if (role === 'SUPERVISOR') return overrides?.supervisorBps ?? DEFAULT_DISCOUNT_CAP_BPS.SUPERVISOR;
  return DEFAULT_DISCOUNT_CAP_BPS[role];
}

export function maxDiscountForRole(role: UserRole, subtotalCents: number, overrides?: DiscountCapOverrides): number {
  const bps = capForRole(role, overrides);
  return Math.floor((subtotalCents * bps) / 10000);
}

export function capLabelForRole(role: UserRole, overrides?: DiscountCapOverrides): string {
  const bps = capForRole(role, overrides);
  if (bps >= 10000) return 'Unlimited';
  return `${(bps / 100).toFixed(0)}%`;
}
